import { Button, Input, Tree, Typography } from "antd-v5";
import classNames from "classnames";
import _, { isNull } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingIcon, SearchIcon } from "./Icons";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import {
  ARMORCODE_GROUP_NONE_VALUE,
  isFallingUnderGroup,
  parseGroupsToHierarchy,
} from "utils/armorcodeGroupUtil";
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";

const { Text } = Typography;

export default function ArmorCodeGroupSelectionList({
  applyGlobalFilter = true,
  disabled,
  onChange,
  value,
  visible,
  allGroups,
  noneOption,
  showSelectAllActions = true
}) {
  const [noData, setNoData] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const treeContainerRef = useRef();
  const visibleNodeRef = useRef({});
  const { globalFilterGroups } = useGlobalFilterContext();

  const onSearch = useCallback((e) => {
    setSearchText(e.target.value);
    requestAnimationFrame(() => {
      setNoData(treeContainerRef.current.offsetHeight === 0);
    });
  }, []);

  const groupHierarchy = useMemo(() => {
    if (_.isEmpty(allGroups)) {
      return [];
    }
    let groups = allGroups;
    if (applyGlobalFilter && !_.isEmpty(globalFilterGroups)) {
      groups = groups.filter((group) => {
        return globalFilterGroups.some((globalFilteredGroup) => {
          return isFallingUnderGroup(globalFilteredGroup, group);
        });
      });
    }
    groups = parseGroupsToHierarchy(groups || [], true).hierarchy;
    if (noneOption && _.isEmpty(globalFilterGroups)) {
      groups.unshift({
        title: `<None>`,
        key: ARMORCODE_GROUP_NONE_VALUE,
        children: [],
        groupValue: ["<None>"],
      });
    }
    return groups;
  }, [allGroups, globalFilterGroups, applyGlobalFilter, noneOption]);

  const onCheck = useCallback(
    ({ checked: selectedKeys }) => {
        onChange(selectedKeys);
    },
    [onChange]
  );

  useEffect(() => {
    if (!visible) {
      setSearchText("");
    }
  }, [visible]);

  const onExpandAll = useCallback(() => {
    if (_.isFunction(onChange) && !_.isEmpty(groupHierarchy)) {
      const keysToExpand = _.flattenDeep(groupHierarchy.map((node) => getAllNodeChildrenKeys(node)))
        .filter((key) => {
          return (
            visibleNodeRef.current[key] ||
            visibleNodeRef.current[key] === undefined
          );
        });
        setExpandedKeys(keysToExpand);
    }
  }, [onChange, groupHierarchy]);
  
  const onSelectAll = useCallback(() => {
    if (_.isFunction(onChange) && !_.isEmpty(groupHierarchy)) {
      const selectedKeys = _.flattenDeep(groupHierarchy.map((node) => getAllNodeChildrenKeys(node)))
        .filter((key) => {
          return (
            visibleNodeRef.current[key] ||
            visibleNodeRef.current[key] === undefined
          );
        });
      onChange(selectedKeys);
    }
  }, [onChange, groupHierarchy]);
  
  const onNodeSelectAll = useCallback((e, nodeData) => {
    e.stopPropagation();
    const nodeChildrenKeys = getAllNodeChildrenKeys(nodeData);
    const finalSelectedKeys = _.uniq([...(value || []), ...nodeChildrenKeys]).filter((key) => {
      return (
        visibleNodeRef.current[key] ||
        visibleNodeRef.current[key] === undefined
      );
    });
    onChange(finalSelectedKeys);
  }, [onChange, value]);

  const onNodeClearAll = useCallback((e, nodeData) => {
    e.stopPropagation();
    const nodeChildrenKeys = getAllNodeChildrenKeys(nodeData);
    const finalSelectedKeys = _.filter(value, (key) => !nodeChildrenKeys.includes(key));
    onChange(finalSelectedKeys);
  }, [onChange, value]);

  const onClearAll = useCallback(() => {
    if (_.isFunction(onChange)) {
      onChange([]);
    }
  }, [onChange]);

  return (
    <div
      className={classNames("armorcode-group-field-content-wrapper-v2", {
        "search-active": !_.isEmpty(searchText),
      })}
    >
      <Input
        suffix={<SearchIcon />}
        className="filter-group-search m-b-m"
        placeholder="Search"
        value={searchText}
        onChange={onSearch}
        allowClear
        disabled={disabled}
      />
      <div className="tree-action-button-wrapper text-right">
        <Button
          className="tree-action-button"
          type="link"
          size="small"
          onClick={onExpandAll}
          disabled={disabled}
        >
          Expand All
        </Button>
        {
          showSelectAllActions &&
          <Button
            className="tree-action-button m-l-l"
            type="link"
            size="small"
            onClick={onSelectAll}
            disabled={disabled}
          >
            Select All
          </Button>
        }
        <Button
          className="tree-action-button m-l-l"
          type="link"
          size="small"
          onClick={onClearAll}
          disabled={disabled}
        >
          Clear All
        </Button>
      </div>
      <div className="group-selection-tree" ref={treeContainerRef} style={{ maxHeight: 300, overflow: "auto" }}>
        <Tree
          checkable
          checkStrictly
          disabled={disabled}
          selectable={false}
          checkedKeys={value}
          onCheck={onCheck}
          treeData={groupHierarchy}
          expandedKeys={expandedKeys}
          onExpand={(expandedKeys) => setExpandedKeys(expandedKeys)}
          titleRender={(nodeData) => {
            return (
              <GroupTile
                nodeData={nodeData}
                selectedKeys={value}
                onSelectAll={onNodeSelectAll}
                onClearAll={onNodeClearAll}
                showSelectAllActions={showSelectAllActions}
              />
            );
          }}
          filterTreeNode={(node) => {
            if (searchText) {
              const match = node.groupValue.includes(searchText);
              // If a node is selected and then a search is made for some other node and select all option is triggered
              // then this will retain the previous selection
              visibleNodeRef.current[node.key] = match || node.checked;
              return match;
            } else {
              visibleNodeRef.current[node.key] = undefined;
            }
            return false;
          }}
        />
      </div>
      {isNull(allGroups) ? (
        <div className="text-center p-y-m">
          <LoadingIcon />
        </div>
      ) : null}
      {noData ? (
        <div className="text-center">
          <Text type="secondary">No data</Text>
        </div>
      ) : null}
    </div>
  );
}

function GroupTile({ nodeData, selectedKeys, onSelectAll, onClearAll, showSelectAllActions }) {

  const isSelected = useMemo(() => {
    return _.some(nodeData.children, ({ key }) => _.includes(selectedKeys, key))
  }, [nodeData, selectedKeys]);

  return (
    <div className="d-flex">
      <span className="text-ellipsis flex-1">{<ACMiddleEllipsis text={nodeData.title} />}</span>
      {
        (!_.isEmpty(nodeData.children) && showSelectAllActions) ?
          <span className="tree-node-action-button-container">
            {
              isSelected ?
                <Button
                  className="tree-node-action-button"
                  type="link"
                  size="small"
                  onClick={(e) => onClearAll(e, nodeData)}
                >
                  Clear All
                </Button>
                :
                <Button
                  className="tree-node-action-button"
                  type="link"
                  size="small"
                  onClick={(e) => onSelectAll(e, nodeData)}
                >
                  Select All
                </Button>
            }
          </span>
          :
          null
      }
    </div>
  )
}

function getAllNodeChildrenKeys(nodeData, keys = []) {
    keys.push(nodeData.key);
    if (!_.isEmpty(nodeData.children)) {
      nodeData.children.forEach((node) => {
        getAllNodeChildrenKeys(node, keys);
      });
    }
    return keys;
}
